import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare } from 'lucide-react';
import { AppRail, type RailItem } from './AppRail';
import { getNavItems, getBottomItems } from '@/components/backoffice/DashboardSidebar';
import { ContactKevinModal } from '@/components/common/ContactKevinButton';

/**
 * The client portal's rail (W1 shell, behind ui_refonte). Same entries as
 * DashboardSidebar (the flag-off path): the portal sections, then settings,
 * help and the support button. The client's name sits under the XDial mark.
 * W5: settings is shown to the viewer, the only role the portal admits (it
 * was filtered to tenant admins, so nobody saw it).
 */
interface Props {
  activeTab: string;
  onTabChange: (id: string) => void;
  companyName?: string | null;
  badges?: Record<string, number | undefined>;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  collapsible?: boolean;
}

export function PortalRail({ activeTab, onTabChange, companyName, badges, collapsed, onCollapsedChange, collapsible = true }: Props) {
  const { t } = useTranslation(['admin', 'dialer', 'pages']);
  const [contactOpen, setContactOpen] = useState(false);
  const toItem = (item: { id: string; label: string; icon: unknown }): RailItem => ({
    id: item.id,
    label: item.label,
    icon: item.icon as RailItem['icon'],
    active: activeTab === item.id,
    badge: badges?.[item.id],
    onSelect: () => onTabChange(item.id),
  });
  const bottom = getBottomItems(t).map(toItem);
  bottom.push({ id: 'contact', label: t('dialer:contactKevin.button'), icon: MessageSquare as unknown as RailItem['icon'], onSelect: () => setContactOpen(true) });
  return (
    <>
      <AppRail
        ariaLabel={t('admin:backoffice.sidebar.navLabel')}
        collapsed={collapsed}
        onCollapsedChange={onCollapsedChange}
        collapsible={collapsible}
        onBrandClick={() => onTabChange('home')}
        header={(open) => (open && companyName ? (
          <p className="truncate px-1 text-[12px]" style={{ color: 'var(--vf-text-3)' }} title={companyName}>{companyName}</p>
        ) : null)}
        groups={[getNavItems(t).map(toItem), bottom]}
      />
      <ContactKevinModal open={contactOpen} onOpenChange={setContactOpen} />
    </>
  );
}

export default PortalRail;
